(function (scope, factory) {
  'use strict';
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (scope) scope.HyfenDesign = api;
})(typeof window === 'undefined' ? null : window, function () {
  'use strict';
  const VERSION = 2;
  const STORAGE_KEY = 'hyfenStudioDesign';
  const STYLES = Object.freeze({
    'Classic Heart': ['h1', 'h2'],
    'Circle': ['c1', 'c2'],
    'Brush Stroke': ['b1', 'b2']
  });

  function placeById(places, id) {
    return places.find(function (place) { return String(place.id) === String(id); }) || null;
  }

  function identity(places, firstId, secondId, style, pride) {
    const first = placeById(places, firstId);
    const second = placeById(places, secondId);
    if (!first || !second || !Object.prototype.hasOwnProperty.call(STYLES, style)) return null;
    const single = first.iso === second.iso;
    const rule = single && pride && pride[first.iso];
    const approved = Boolean(rule && rule.status === 'approved' && typeof rule.message === 'string' &&
      rule.message.trim() && !/two countries|two nations|boring/i.test(rule.message));
    return Object.freeze({
      schemaVersion: VERSION,
      first: { id: first.id, iso: first.iso, label: first.country },
      second: { id: second.id, iso: second.iso, label: second.country },
      style: style,
      mode: single ? 'single' : 'dual',
      nameLine: single ? first.country : first.country + ' × ' + second.country,
      prideMessage: approved ? rule.message.trim() : null,
      wordingStatus: single ? (approved ? 'approved' : 'review_required') : 'customily_managed'
    });
  }

  function withTagline(design, choice) {
    if (!design) return null;
    const mode = ['keep', 'edit', 'remove'].includes(choice?.mode) ? choice.mode : 'keep';
    const text = String(choice?.text || '').replace(/[\u0000-\u001f\u007f]/g, ' ').slice(0, 40);
    return Object.freeze(Object.assign({}, design, { tagline: Object.freeze({ mode, text }) }));
  }

  function serialize(design) {
    if (!design) return null;
    return {
      schemaVersion: VERSION,
      countryOne: design.first.label,
      countryOneId: design.first.id,
      countryOneIso: design.first.iso,
      countryTwo: design.second.label,
      countryTwoId: design.second.id,
      countryTwoIso: design.second.iso,
      style: design.style,
      mode: design.mode,
      tagline: design.tagline || { mode: 'keep', text: '' }
    };
  }

  function read(storage, places, pride) {
    try {
      const saved = JSON.parse(storage.getItem(STORAGE_KEY) || 'null');
      if (!saved || (saved.schemaVersion && saved.schemaVersion !== VERSION)) return null;
      // IDs are authoritative; never trust text, mode or messages from browser storage.
      return withTagline(identity(places, saved.countryOneId, saved.countryTwoId, saved.style, pride), saved.tagline);
    } catch (_) { return null; }
  }

  function save(storage, design) {
    try { storage.setItem(STORAGE_KEY, JSON.stringify(serialize(design))); return true; }
    catch (_) { return false; }
  }

  function availability(product, design) {
    if (!design || !product || !product.enabled) return { available: false, reason: 'unavailable' };
    if (!Array.isArray(product.styles) || !product.styles.includes(design.style)) {
      return { available: false, reason: 'style_unavailable' };
    }
    if (design.mode === 'single' && design.wordingStatus !== 'approved') {
      return { available: false, reason: 'wording_review' };
    }
    if (design.mode === 'single' && !product.singleIdentityReady) {
      return { available: false, reason: 'single_mapping_pending' };
    }
    return { available: true, reason: null };
  }

  function productUrl(product, design, baseOrigin) {
    if (!availability(product, design).available) return null;
    let url;
    try {
      url = new URL(product.url, baseOrigin);
      if (url.origin !== new URL(baseOrigin).origin || !/\/products\/[^/]+\/?$/.test(url.pathname)) return null;
    } catch (_) { return null; }
    const aliases = product.placeValues || {};
    url.searchParams.set('hyfen_country_1', aliases[design.first.iso] || design.first.label);
    url.searchParams.set('hyfen_country_2', aliases[design.second.iso] || design.second.label);
    url.searchParams.set('hyfen_style', design.style);
    // Customily's hidden Template Mode dropdown maps these values to the verified
    // Dual-Identity and Single-Identity templates.
    url.searchParams.set('hyfen_template_mode', design.mode);
    const tagline = design.tagline || { mode: 'keep', text: '' };
    url.searchParams.set('hyfen_tagline_mode', ['edit', 'remove'].includes(tagline.mode) ? tagline.mode : 'keep');
    if (tagline.mode === 'edit' && tagline.text) url.searchParams.set('hyfen_tagline_text', tagline.text);
    // Customily's product-page initializer is sensitive to form-style '+' space
    // encoding in handoff values such as "United States" and "Brush Stroke".
    // Preserve normal query semantics but serialize spaces as %20 so third-party
    // code that reads the raw query string receives an unambiguous value.
    return url.pathname + url.search.replace(/\+/g, '%20') + url.hash;
  }

  function previewUrls(assetRoot, places, firstId, secondId, style) {
    const first = placeById(places, firstId), second = placeById(places, secondId);
    const prefixes = STYLES[style];
    if (!first || !second || !prefixes) return [];
    return [assetRoot + 'hyfen-' + prefixes[0] + '-' + first.filename,
      assetRoot + 'hyfen-' + prefixes[1] + '-' + second.filename];
  }

  function productPlan(product, design, back) {
    const available = availability(product, design);
    if (!available.available) return { ready: false, reason: available.reason };
    const option = (product.backOptions || []).find(function (entry) { return entry.id === back; });
    if (!option || !option.verified || !Array.isArray(option.modes) || !option.modes.includes(design.mode)) {
      return { ready: false, reason: 'back_mapping_pending' };
    }
    return { ready: true, productId: product.id, identity: serialize(design), back: option.id };
  }

  return Object.freeze({
    VERSION: VERSION, STORAGE_KEY: STORAGE_KEY, STYLES: STYLES,
    identity: identity, withTagline: withTagline, serialize: serialize, read: read, save: save,
    availability: availability, productUrl: productUrl, previewUrls: previewUrls,
    productPlan: productPlan
  });
});
